import { ComponentFactoryResolver, EventEmitter, Inject, Injectable, OnDestroy, ViewContainerRef } from '@angular/core';
import { FeedbackQuestionSurvey } from '../providers/question-provider';
import { ArrayUtils } from '../../common/providers/array-utils';
import { Router } from '@angular/router';
import * as questionsComponents from '../questions';
import BaseQuestion from './base-question';
import { AthleteQuestionAnswerProvider } from '../providers/athlete-question-answer-provider';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription } from 'rxjs';
import { FeedbackQuestionService } from './feedback-question.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { importToArray } from "../../common/providers/utils-provider.service";
import { Organization } from "../../common/models/organization";
import { Athlete } from "../../common/models/athlete";
import { Feedback } from "../../common/models/feedback";
import { Timeslot } from "../../common/models/timeslot";
import { HasPendingChangesGuard } from "../../common/guards/has-pending-changes";
import { Snackbar } from "../../common/snackbar/snackbar";
import { FeedbackAthleteQuestionAnswer } from "../../common/models/feedback-athlete-question-answer";
import { SnackbarData } from "../../common/snackbar/snackbar-data";

const QUESTION_COMPONENTS = importToArray(questionsComponents);
const QUESTIONS_COMPONENTS_MAP = QUESTION_COMPONENTS.reduce<any>((result: any, value: any, index, array) => {
  result[value.type] = value;
  return result;
}, {});

@Injectable({
  providedIn: 'root'
})
export class FeedbackQuestionFactoryService implements OnDestroy {

  public surveyFinished: EventEmitter<any> = new EventEmitter();
  public surveySaved: EventEmitter<any> = new EventEmitter();

  public organization: Organization = {} as Organization;
  public athlete: Athlete = {} as Athlete;
  public feedback: Feedback = {} as Feedback;
  public timeslot: Timeslot = {} as Timeslot;
  public submitSubject: Subject<boolean> = new Subject();

  private _questions: FeedbackQuestionSurvey[] = [];
  private factoryResolver;
  private rootViewContainer: any;
  private questionComponent: BaseQuestion = {} as BaseQuestion;
  private athleteAnswers: any = [];

  private answeredQuestions: any[] = [];
  private subscriptions: Subscription[] = [];

  constructor(
    @Inject(ComponentFactoryResolver) factoryResolver: ComponentFactoryResolver,
    private arrayUtils: ArrayUtils,
    private router: Router,
    private athleteQuestionAnswerProvider: AthleteQuestionAnswerProvider,
    private toaster: MatSnackBar,
    private translator: TranslateService,
    private changesGuard: HasPendingChangesGuard,
    private snackbar: Snackbar,
    private feedbackQuestionService: FeedbackQuestionService,
  ) {
    this.factoryResolver = factoryResolver;
    this.subscribeToSurveyFinished();
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  set questions(questions: FeedbackQuestionSurvey[]) {
    this._questions = questions;
  }

  public setRootViewContainerRef(viewContainerRef: ViewContainerRef) {
        this.rootViewContainer = viewContainerRef;
    }

    public setOrganization(organization: Organization) {
        this.organization = organization;
    }

    public setAthlete(athlete: Athlete) {
        this.athlete = athlete;
    }

    public setFeedback(feedback: Feedback) {
        this.feedback = feedback;
    }

    public setTimeslot(timeslot: Timeslot) {
        this.timeslot = timeslot;
    }

    public startSurvey() {
        this.answeredQuestions = [];
        this.athleteAnswers = [];

        this.feedbackQuestionService.reset();

        if (this._questions.length > 0) {
            this.loadQuestionComponent(this.feedback, this._questions[0]);
        }
    }

    private loadQuestionComponent(feedback: Feedback, question: FeedbackQuestionSurvey, previousAnswers: any = null, answeredAnswers: any = null) {
        const factory = this.factoryResolver
            .resolveComponentFactory(QUESTIONS_COMPONENTS_MAP[question.type]);

        this.rootViewContainer.clear();

        const componentRef = this.rootViewContainer.createComponent(factory);
        const component = componentRef.instance as BaseQuestion;
        component.question = question;
        component.feedback = feedback;
        component.previousAnswers = previousAnswers;
        component.answeredAnswers = answeredAnswers;
        component.hasAnsweredQuestion = this.answeredQuestions.length > 0;

        this.questionComponent = component;
        this.subscribeToAnswerReceived();
        this.subscribeToNextQuestionClicked();
        this.subscribeToPreviousQuestionClicked();
    }

    private subscribeToAnswerReceived(): void {
        this.subscriptions.push(
            this.questionComponent
                .answerReceived.subscribe((event) => {
                    // this.loadNextQuestionComponent(event.nextQuestionId);
                })
        );
        // this.subscriptions.push(subscription);
    }

    private subscribeToNextQuestionClicked(): void {
        this.subscriptions.push(
            this.questionComponent
                .nextQuestionClicked.subscribe((data: {question: any; answers: any; isBack: boolean}) => {
                    const answeredAnswers: any[] = [];
                    // avoid duplicate user answers by removing already answered questions answers.
                    // for example in case og back btn pressed.
                    this.athleteAnswers = this.athleteAnswers.filter(function( obj :any) {
                        return obj.questionId !== data.question.id;
                    });

                    for (const answer of data['answers']) {
                        const faqa:any = {} as FeedbackAthleteQuestionAnswer;
                        // faqa.athleteId = this.athlete.id;
                        // faqa.feedbackId = this.feedback.id;
                        faqa['questionId'] = data.question.id;
                        faqa.answeredAt = new Date();
                        faqa.questionAnswerId = answer.questionAnswerId;
                        faqa.userData = answer.userData || null;

                        this.athleteAnswers.push(faqa);
                        answeredAnswers.push(answer);
                    }

                    this.feedbackQuestionService.addAnswerToAnsweredAnswers({question: {id: data.question.id}, answers: data['answers']});

                    // do noting as back button pressed is handled by backClicked event
                    if (data.isBack) {
                        return;
                    }

                    this.changesGuard.registerChanges(() => {
                        // do nothing as the user must select the answer
                        this.snackbar.show(new SnackbarData('FeedbackNeedToComplete'));
                    });

                    if (0 < data.answers.length) {
                        // save answered questions + answers for back button
                        this.answeredQuestions.push({question: data['question'].id, answers: answeredAnswers});

                        this.loadNextQuestionComponent(data.answers[0].nextQuestionId, null, this.athleteAnswers);
                    }
                })
        );
    }

    private subscribeToPreviousQuestionClicked(): void {
        this.subscriptions.push(
            this.questionComponent.previousQuestionClicked.subscribe((data: any) => {
                // get previous question id and remove it from answered question array
                // this.feedbackQuestionService.addAnswerToAnsweredAnswers({question: {id: data.question.id}, answers: data['answers']});

                const previousQuestion = this.answeredQuestions.pop();
                if (typeof previousQuestion === 'undefined') {
                    return;
                }

                this.loadNextQuestionComponent(previousQuestion.question, previousQuestion.answers);
            })
        );
    }

    private subscribeToSurveyFinished() {
        this.subscriptions.push(
            this.surveyFinished.subscribe(() => {
                this.submitSubject.next(true);

                const data = new SnackbarData('FeedbackSaved');

                this.subscriptions.push(
                    this.athleteQuestionAnswerProvider.save(this.organization, this.feedback, this.athlete, this.athleteAnswers, this.timeslot).subscribe({
                      next: () => {
                        this.changesGuard.unregisterChanges();
                        this.surveySaved.emit({
                          organizationCode: this.organization.code,
                          athleteId: this.athlete.id,
                          userId: this.athlete.userId,
                          sessionDate: this.timeslot ? this.timeslot.fromDate : null
                        });
                        this.snackbar.show(data);
                      },
                      error: (err) => {
                        data.setError(err);
                        this.snackbar.show(data);
                      },
                      complete: () => {
                        this.submitSubject.next(false);
                      }
                    })
                );
            })
        );
    }

    private loadNextQuestionComponent(nextQuestionId: number|null = null, previousAnswers: any = null, answeredAnswers: any = null): void {
        // no next question ID ? So, finish the survey
        if (null === nextQuestionId) {
            this.surveyFinished.emit();
            return;
        }

        const nextQuestion = this.arrayUtils.findById(this._questions, nextQuestionId);
        this.loadQuestionComponent(this.feedback, nextQuestion, previousAnswers, answeredAnswers);
    }
}
